import {LocalDate} from "@js-joda/core";

export type Employee = {
    id : number;
    firstName : string;
    lastName : string;
    teamId : number;
    type : 'employee' | 'subcontractor';
    isTimely : boolean;
    currentTimelyStreak : number;
    practiceAreas : string[];
    targetHoursPerWeek : number;
    salesLiaisons : Array<{
        id : number;
        project : string;
    }>;
};

export type Project = {
    id : number;
    name : string;
    type : string;
    teamId : number;
    jiraKey ?: string;
    status ?: string;
};

export type WeekActuals = {
    startOfWeek : string;
    totalTime : number;
};

export type ProjectActuals = {
    id : number;
    weekActuals : WeekActuals[];
};

export type EmployeeActuals = {
    employeeId : number;
    projects : ProjectActuals[];
};

export type Holiday = {
    Holiday : string;
    HolidayDate : string;
    isWeekend : boolean;
};

export type CapacityConfigItem = {
    calendar : number;
    fiscal : number;
    fiscalend : string;
    fiscalstart : string;
    hours : number;
};

export type CapacityConfig = Record<string, CapacityConfigItem>;

export type Schedule = {
    projectId : number;
    employeeId : number;
    locked : boolean;
    weeks : number[];
};

export type ProjectScheduleWeek = {
    startOfWeek : string;
    scheduled : number;
    actual ?: number;
};

export type ProjectSchedule = {
    locked : boolean;
    weeks : ProjectScheduleWeek[];
    project : Project;
};

export type EmployeeSchedule = {
    employee : Employee;
    schedules : ProjectSchedule[];
};

export type ClientServiceTeam = {
    id : number;
    name : string;
    abbreviation : string;
    associates : Array<{
        associateId : number;
        reporting : boolean;
    }>;
}

export const UNKNOWN_TEAM = {
    id : -1,
    name : 'Unknown',
    abbreviation : 'UNK',
    associates : [],
}

export const compileSchedules = (
    firstSunday : LocalDate,
    employees : Employee[],
    projects : Project[],
    schedules : Schedule[],
    employeeActuals : EmployeeActuals[] | null,
) : EmployeeSchedule[] => {
    const weeks = getEightWeeks(firstSunday);
    const projectsById = new Map(projects.map(project => ([project.id, project])));
    const employeesById = new Map(employees.map(employee => ([employee.id, employee])));

    const actuals = new Map(!employeeActuals ? [] : employeeActuals.map(employeeActual => [
        employeeActual.employeeId,
        new Map(employeeActual.projects.map(projectActuals => [
            projectActuals.id,
            new Map(projectActuals.weekActuals.map(weekActuals => [
                weekActuals.startOfWeek,
                weekActuals.totalTime,
            ])),
        ])),
    ]));

    const sortedSchedules = [...schedules].sort((a, b) => a.employeeId - b.employeeId);

    const employeeSchedules : EmployeeSchedule[] = [];
    let employeeSchedule : EmployeeSchedule | null = null;

    for (const schedule of sortedSchedules) {
        const project = projectsById.get(schedule.projectId);

        if (!project) {
            continue;
        }

        if (!employeeSchedule || employeeSchedule.employee.id !== schedule.employeeId) {
            const employee = employeesById.get(schedule.employeeId);

            if (!employee) {
                continue;
            }

            employeesById.delete(schedule.employeeId);
            employeeSchedules.push(employeeSchedule = {employee, schedules: []});
        }

        const employeeId = employeeSchedule.employee.id;

        employeeSchedule.schedules.push({
            locked: schedule.locked,
            project,
            weeks: schedule.weeks.map((scheduled, weekNo) => {
                let actual = undefined;
                const projectActuals = actuals.get(employeeId);

                if (projectActuals) {
                    const weekActuals = projectActuals.get(project.id);

                    if (weekActuals) {
                        actual = weekActuals.get(weeks[weekNo].key);
                        weekActuals.delete(weeks[weekNo].key);

                        if (weekActuals.size === 0) {
                            projectActuals.delete(project.id);

                            if (projectActuals.size === 0) {
                                actuals.delete(employeeId);
                            }
                        }
                    }
                }

                return {startOfWeek: weeks[weekNo].key, scheduled, actual};
            }),
        });
    }

    employeeSchedules.push(...[...employeesById.values()].map(employee => ({
        employee,
        schedules: [],
    })));

    employeeSchedules.sort(
        ({employee : a}, {employee : b}) => `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`)
    ).map(employeeSchedule => {
        const projectActuals = actuals.get(employeeSchedule.employee.id);

        if (projectActuals) {
            for (const [projectId, weekActuals] of projectActuals.entries()) {
                const project = projectsById.get(projectId);

                if (!project) {
                    continue;
                }

                employeeSchedule.schedules.push({
                    project,
                    locked: false,
                    weeks: new Array(8).fill(0).map((_, weekNo) => ({
                        startOfWeek: weeks[weekNo].key,
                        scheduled: 0,
                        actual: weekActuals.get(weeks[weekNo].key),
                    })),
                });
            }
        }

        employeeSchedule.schedules.sort(({project : a}, {project : b}) => a.name.localeCompare(b.name));
        return employeeSchedule;
    });

    return employeeSchedules;
};

export type Week = {
    key : string;
    startDate : LocalDate;
};

export const getEightWeeks = (firstSunday : LocalDate) : Week[] => {
    return [...new Array(8).keys()].map(weekNo => {
        const date = firstSunday.plusWeeks(weekNo);
        return {key : date.toString(), startDate: date};
    });
}
